import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"
import { useDeviceInfo } from "@/hooks/use-device-info"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-[colors,box-shadow,transform] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          /* shadow-soft gives filled buttons a subtle plate off the page */
          "bg-primary text-primary-foreground hover:brightness-90 shadow-soft active:scale-[0.97]",
        destructive:
          "bg-destructive text-destructive-foreground hover:brightness-90 shadow-soft active:scale-[0.97]",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground active:scale-[0.97]",
        secondary:
          "bg-secondary text-secondary-foreground hover:brightness-90 active:scale-[0.97]",
        ghost: "hover:bg-accent hover:text-accent-foreground active:scale-[0.97]",
        /* link is inline text — no press-scale, only the underline cue */
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
        // Mobile-optimized sizes with 44px minimum touch targets
        mobile: "h-11 px-6 py-3 text-base",
        "mobile-sm": "h-10 px-4 py-2",
        "mobile-lg": "h-12 px-8 py-4 text-base",
        "mobile-icon": "h-11 w-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  touchOptimized?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, touchOptimized = true, type = "button", ...props }, ref) => {
    const { isMobile } = useDeviceInfo()
    const Comp = asChild ? Slot : "button"
    
    // Auto-adjust size for mobile if touchOptimized is enabled
    const adjustedSize = isMobile && touchOptimized && !size ? "mobile" : size
    
    return (
      <Comp
        className={cn(buttonVariants({ variant, size: adjustedSize, className }))}
        ref={ref}
        // Defaults to type="button" so a button inside a form never submits
        // it by accident (real submit buttons pass type="submit"). Skipped for
        // asChild, which often renders a link that must not carry a type.
        {...(!asChild && { type })}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
